import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { appKeys } from "../keys/appKeys";
import { getInstanceIdFromLocation } from "./getInstanceIdFromLocation";

type UseSilentRefetchParams<TData, TParams> = {
  fetchDataFunction: (params: TParams) => Promise<TData>;
  params: TParams;
  interval: number; // Interval in milliseconds
  compareData?: (prevData: TData, newData: TData) => boolean; // Optional custom comparison function
  dataFromConfig?: any;
};

function useSilentRefetch<TData, TParams>({
  fetchDataFunction,
  params,
  interval,
  compareData,
  dataFromConfig,
}: UseSilentRefetchParams<TData, TParams>) {
  const [data, setData] = useState<any>(dataFromConfig);
  const location = useLocation();
  const queryClient = useQueryClient();

  const instanceId = getInstanceIdFromLocation(location);
  useEffect(() => {
    let timerId: NodeJS.Timer;
    if (
      location.search.split("?")[1] !== undefined &&
      location.search.includes("?configs=")
    ) {
      if (interval < 0) return;
      const fetchData = async () => {
        try {
          const newData = await fetchDataFunction(params);
          if (
            !data ||
            (compareData
              ? compareData(data, newData)
              : JSON.stringify(data) !== JSON.stringify(newData))
          ) {
            setData(newData);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      timerId = setInterval(fetchData, interval);

      return () => clearInterval(timerId);
    } else {
      if (interval < 0) return;
      const invalidateQuery = async () => {
        try {
          const data = await queryClient.fetchQuery(appKeys.app(instanceId));
          setData(data);
        } catch (error) {
          console.error("Error fetching query data:", error);
        }
      };

      timerId = setInterval(invalidateQuery, interval);

      return () => clearInterval(timerId);
    }
  }, [
    fetchDataFunction,
    params,
    interval,
    data,
    compareData,
    location.pathname,
    instanceId,
    location.search,
    queryClient,
    dataFromConfig,
  ]);

  return data;
}

export default useSilentRefetch;
